import { LANDING_PAGE_REDIRECT } from "@/abilities/shop/config/constants";
import { GenericShop } from "@/abilities/shop/types/generic-shop";
import { getI18n } from "@/shared/i18n/i18n";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import * as consumerGraphTypes from "@/shared/services/graphql/generated/consumer-graph-types";
import { provideApolloClient } from "@vue/apollo-composable";

/** Lazy getter for ShopSessionStatus enum. */
const getShopSessionStatus = () => consumerGraphTypes.ShopSessionStatus;

/** Lazy getter for auth redirect statuses. */
const getAuthRedirectStatuses = () => {
    const ShopSessionStatus = getShopSessionStatus();
    return [ShopSessionStatus.notAuthenticated, ShopSessionStatus.authExpired, ShopSessionStatus.authError];
};

/** Lazy getter for error statuses. */
const getErrorStatuses = () => {
    const ShopSessionStatus = getShopSessionStatus();
    return [
        ShopSessionStatus.authError,
        ShopSessionStatus.validationError,
        ShopSessionStatus.rateLimited,
        ShopSessionStatus.serviceUnavailable,
    ];
};

/** Lazy getter for useGenericShopSessionLazyQuery. */
const getUseGenericShopSessionLazyQuery = () => consumerGraphTypes.useGenericShopSessionLazyQuery;

/** Lazy getter for useGenericShopAuthStartUrlLazyQuery. */
const getUseGenericShopAuthStartUrlLazyQuery = () => consumerGraphTypes.useGenericShopAuthStartUrlLazyQuery;

/** True when the user should be redirected to `session.loginPath`. */
export const requiresAuthentication = (session: GenericShop.Session): boolean =>
    getAuthRedirectStatuses().includes(session.status);

/**
 * True when the landing page may start the shop login by itself: the user is
 * not logged in, or the user's login has expired.
 * Unlike `requiresAuthentication()`, this is false for `AUTH_ERROR`: the user
 * sees the error instead of being sent to the shop login again (decided in QIT-7263).
 */
export const canStartLogin = (session: GenericShop.Session): boolean => {
    const ShopSessionStatus = getShopSessionStatus();
    return [ShopSessionStatus.notAuthenticated, ShopSessionStatus.authExpired].includes(session.status);
};

/** True when the shop reported a problem rather than a normal, usable session. */
export const isSessionError = (session: GenericShop.Session): boolean => getErrorStatuses().includes(session.status);

/** True when the shop is usable right now — online, authenticated, no error. */
export const isSessionReady = (session: GenericShop.Session): boolean => {
    const ShopSessionStatus = getShopSessionStatus();
    return session.online && session.status === ShopSessionStatus.connected;
};

/**
 * Builds the request context the middleware needs to construct the auth
 * redirect. `returnPath` must be a relative path — the backend rejects
 * absolute URLs (`isValidReturnPath` in generic-shop.resolver.ts), which is
 * what keeps an attacker from redirecting the user off-domain after login.
 */
const buildRequestContext = (overrides?: Partial<GenericShop.RequestContext>): GenericShop.RequestContext => ({
    returnPath: LANDING_PAGE_REDIRECT,
    locale: getI18n().global.locale.value,
    ...overrides,
});

const loadSession = async (): Promise<GenericShop.Session | null> => {
    try {
        const { load } = getUseGenericShopSessionLazyQuery()();
        const data = await load();
        return data ? { online: data.shopSession.online, status: data.shopSession.status } : null;
    } catch (error) {
        console.warn("Generic shop: session could not be resolved.", error);
        return null;
    }
};

const loadAuthStartUrl = async (context: GenericShop.RequestContext): Promise<string | undefined> => {
    try {
        const { load } = getUseGenericShopAuthStartUrlLazyQuery()({ context });
        const data = await load();
        return data ? data.shopAuthStartUrl.url : undefined;
    } catch (error) {
        console.warn("Generic shop: auth start url could not be resolved.", error);
        return undefined;
    }
};

/**
 * Resolves the generic shop session — whether the shop is reachable and where
 * the user stands with authentication. This is the only state the frontend
 * needs that is not per-item; everything else is resolved backend-side from the
 * authenticated session (no basketId, no shop token in the SPA).
 *
 * The login URL is only fetched when the status calls for authentication, so a
 * connected user costs a single round trip.
 *
 * @param context overrides for the middleware request context; by default the
 *                user returns to the shop landing page in the active UI locale.
 */
export const loadGenericShopSession = async (
    context?: Partial<GenericShop.RequestContext>
): Promise<GenericShop.Session> => {
    provideApolloClient(consumerClient);

    const session = await loadSession();
    if (!session) {
        // The query itself failed, so the shop cannot be talked to at all. The
        // backend has no "unknown" status; SERVICE_UNAVAILABLE carries exactly
        // this meaning and keeps `status` non-optional for consumers.
        const ShopSessionStatus = getShopSessionStatus();
        return { online: false, status: ShopSessionStatus.serviceUnavailable };
    }

    if (!requiresAuthentication(session)) {
        return session;
    }

    return { ...session, loginPath: await loadAuthStartUrl(buildRequestContext(context)) };
};
